// apps/web/src/pages/LoginPage.tsx
// Phase 1 实现 — 登录页面，含表单验证

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { loginApi } from "@/api/auth";
import { useAuthStore } from "@/stores/authStore";
import toast from "react-hot-toast";
import type { AuthResponse } from "@examfast/shared-types";

const schema = z.object({
  email: z.string().email("请输入有效的邮箱"),
  password: z.string().min(1, "请输入密码"),
});

type FormValues = z.infer<typeof schema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);

  const mutation = useMutation({
    mutationFn: loginApi,
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken);
      setUser(data.user);
      toast.success("登录成功");
      navigate("/", { replace: true });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "登录失败");
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 bg-white">
      {/* Logo / 标题 */}
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-primary">Exam-Fast</h1>
        <p className="text-gray-400 text-sm mt-1">登录以继续使用</p>
      </div>

      {/* 表单 */}
      <form
        onSubmit={handleSubmit((vals) => mutation.mutate(vals))}
        className="space-y-4"
        noValidate
      >
        {/* 邮箱 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">邮箱</label>
          <input
            type="email"
            autoComplete="email"
            {...register("email")}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
                       text-sm bg-gray-50/50"
            placeholder="you@example.com"
          />
          {errors.email && (
            <p className="text-danger text-xs mt-1">{errors.email.message}</p>
          )}
        </div>

        {/* 密码 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">密码</label>
          <input
            type="password"
            autoComplete="current-password"
            {...register("password")}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
                       text-sm bg-gray-50/50"
            placeholder="请输入密码"
          />
          {errors.password && (
            <p className="text-danger text-xs mt-1">{errors.password.message}</p>
          )}
        </div>

        {/* 提交按钮 */}
        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold
                     rounded-xl transition-colors disabled:opacity-50 btn-tap"
        >
          {mutation.isPending ? "登录中..." : "登录"}
        </button>
      </form>

      {/* 去注册 */}
      <p className="text-center text-sm text-gray-400 mt-6">
        没有账号？
        <Link to="/register" className="text-primary font-medium ml-1">
          去注册
        </Link>
      </p>
    </div>
  );
}
